import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import {
  Phone,
  Send,
  Home,
  Zap,
  HelpCircle,
  Info,
  Mail,
} from 'lucide-react';

export default function Footer() {
  const { theme } = useTheme();
  const { t } = useTranslation();

  // Smooth scroll helper
  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <footer
      className={`border-t ${
        theme === 'dark'
          ? 'bg-[#0B0D12] border-gray-800'
          : 'bg-white border-gray-200'
      } py-8 px-6`}
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 text-center">
          {/* Explore Links */}
          <div className="text-center">
            <h4
              className={`text-base font-semibold mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}
            >
              {t('footer.explore') || 'Explore'}
            </h4>
            <ul className="space-y-2 text-center">
              <li>
                <a
                  href="#home"
                  onClick={(e) => scrollToSection(e, 'home')}
                  className={`flex items-center justify-center gap-2 text-sm ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-orange-400'
                      : 'text-gray-600 hover:text-orange-500'
                  } transition-colors duration-300`}
                >
                  <Home className="w-3.5 h-3.5" />
                  {t('navbar.home')}
                </a>
              </li>
              <li>
                <a
                  href="#features"
                  onClick={(e) => scrollToSection(e, 'features')}
                  className={`flex items-center justify-center gap-2 text-sm ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-orange-400'
                      : 'text-gray-600 hover:text-orange-500'
                  } transition-colors duration-300`}
                >
                  <Zap className="w-3.5 h-3.5" />
                  {t('navbar.features')}
                </a>
              </li>

            </ul>
          </div>

          {/* Support Links */}
          <div className="text-center">
            <h4
              className={`text-base font-semibold mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}
            >
              {t('footer.support') || 'Support'}
            </h4>
            <ul className="space-y-2 text-center">
              <li>
                <a
                  href="#faq"
                  onClick={(e) => scrollToSection(e, 'faq')}
                  className={`flex items-center justify-center gap-2 text-sm ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-orange-400'
                      : 'text-gray-600 hover:text-orange-500'
                  } transition-colors duration-300`}
                >
                  <HelpCircle className="w-3.5 h-3.5" />
                  {t('navbar.faq') || 'FAQ'}
                </a>
              </li>
              <li>
                <a
                  href="#about"
                  onClick={(e) => scrollToSection(e, 'about')}
                  className={`flex items-center justify-center gap-2 text-sm ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-orange-400'
                      : 'text-gray-600 hover:text-orange-500'
                  } transition-colors duration-300`}
                >
                  <Info className="w-3.5 h-3.5" />
                  {t('navbar.aboutUs')}
                </a>
              </li>
            </ul>
          </div>

          {/* Account Links */}
          <div className="text-center">
            <h4
              className={`text-base font-semibold mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}
            >
              {t('footer.account')}
            </h4>
            <ul className="space-y-2 text-center">
              <li>
                <Link
                  to="/login"
                  className={`block text-sm ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-orange-400'
                      : 'text-gray-600 hover:text-orange-500'
                  } transition-colors duration-300`}
                >
                  {t('navbar.login')}
                </Link>
              </li>
              <li>
                <Link
                  to="/register"
                  className={`block text-sm ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-orange-400'
                      : 'text-gray-600 hover:text-orange-500'
                  } transition-colors duration-300`}
                >
                  {t('navbar.getStarted')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="text-center">
            <h4
              className={`text-base font-semibold mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}
            >
              Contact
            </h4>
            <ul className="space-y-2 text-center">
              <li>
                <a
                  href="tel:0707730375"
                  className={`flex items-center justify-center gap-2 text-sm ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-orange-400'
                      : 'text-gray-600 hover:text-orange-500'
                  } transition-colors duration-300`}
                >
                  0707730375
                </a>
              </li>
              <li>
                <a
                  href="tel:0796219146"
                  className={`flex items-center justify-center gap-2 text-sm ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-orange-400'
                      : 'text-gray-600 hover:text-orange-500'
                  } transition-colors duration-300`}
                >
                  0796219146
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          className={`mt-6 pt-6 border-t ${
            theme === 'dark' ? 'border-gray-800' : 'border-gray-200'
          }`}
        >
          <p
            className={`text-center text-xs ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
            }`}
          >
            © {new Date().getFullYear()} {t('footer.allRightsReserved')}
          </p>
        </div>
      </div>
    </footer>
  );
}
